import React, { ReactNode, useMemo } from "react";
import styled, { css, DefaultTheme } from "styled-components";
import { TriangleAlert } from "lucide-react";
import { PRICE_LADDER_DETAILS_ID } from "testIds";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import {
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  newLookType,
} from "common/NewLook/tokens.ts";
import Pill from "common/NewLook/Pill.tsx";
import {
  formatFilledPercent,
  LadderKind,
  LadderLevel,
  LevelsLadderModel,
} from "common/NewLook/priceLadder.ts";
import {
  formatTradeBarChange,
  getTradeBarOrders,
  TradeBarOrders,
  TradeBarPoints,
} from "common/NewLook/tradeBarLayout.ts";
import {
  getTradeBarOrderRows,
  TRADE_BAR_ROW_LIMIT,
  TradeBarOrderRow,
  TradeBarTarget,
} from "common/NewLook/tradeBarHover.ts";

export type TradeBarDetailsSize = "card" | "sheet";

interface TradeBarDetailsProps {
  model: LevelsLadderModel;
  points: TradeBarPoints;
  target: TradeBarTarget;
  formatPrice: (price: number) => string;
  toneColor: (tone: LadderKind) => string;
  size: TradeBarDetailsSize;
  showAll?: boolean;
  onShowAllChange?: (showAll: boolean) => void;
}

type DetailsContext = TradeBarDetailsProps & {
  orders: TradeBarOrders;
  t: (
    key: TranslationKeys<LanguageNameSpaces.TRADES>,
    options?: object
  ) => string;
};

type OrderKind = "entry" | "take_profit";

interface Stat {
  label: string;
  value: string;
  change?: boolean;
}

interface ValueLine {
  key: string;
  label: string;
  value?: string;
}

const TradeBarDetails = (props: TradeBarDetailsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const orders = useMemo(() => getTradeBarOrders(props.model), [props.model]);
  const context: DetailsContext = { ...props, orders, t };
  return (
    <Root
      $size={props.size}
      data-testid={PRICE_LADDER_DETAILS_ID}
      data-target={props.target.kind}
    >
      {props.target.kind === "entry" && (
        <OrdersDetails context={context} kind="entry" />
      )}
      {props.target.kind === "take_profit" && (
        <OrdersDetails context={context} kind="take_profit" />
      )}
      {props.target.kind === "price" && <PriceDetails context={context} />}
      {props.target.kind === "stop" && <StopDetails context={context} />}
    </Root>
  );
};

export default TradeBarDetails;

const change = (orders: TradeBarOrders, price: number, base?: number) =>
  base ? formatTradeBarChange(price, base, orders.isShort) : undefined;

const fromPrice = (context: DetailsContext, price: number) =>
  context.orders.isClosed
    ? undefined
    : change(context.orders, price, context.orders.currentPrice);

const levelsOf = (orders: TradeBarOrders, kind: OrderKind) =>
  kind === "entry" ? orders.entries : orders.takeProfits;

const nextIndexOf = (orders: TradeBarOrders, kind: OrderKind) => {
  const levels = levelsOf(orders, kind);
  const index =
    kind === "entry" ? orders.nextEntryIndex : orders.nextTakeProfitIndex;
  return orders.isClosed || levels[index]?.filled ? -1 : index;
};

const focusOf = (context: DetailsContext, kind: OrderKind) => {
  const { target, orders } = context;
  if (target.kind === kind && target.index >= 0) return target.index;
  return Math.max(nextIndexOf(orders, kind), 0);
};

const levelName = (context: DetailsContext, kind: OrderKind, index: number) => {
  const number = levelsOf(context.orders, kind)[index].number ?? index + 1;
  return kind === "entry"
    ? context.t("trade_bar.entry_number", { number })
    : context.t("trade_bar.take_profit_number", { number });
};

const TITLE_KEYS = {
  entry: {
    filled: "trade_bar.entries_filled",
    progress: "trade_bar.entries_progress",
    plain: "trade_bar.entries",
  },
  take_profit: {
    filled: "trade_bar.take_profits_filled",
    progress: "trade_bar.take_profits_progress",
    plain: "trade_bar.take_profits",
  },
} as const;

const ordersTitle = (context: DetailsContext, kind: OrderKind) => {
  const { model, orders, t } = context;
  const levels = levelsOf(orders, kind);
  const progress =
    kind === "entry" ? model.progress?.entry : model.progress?.takeProfit;
  if (model.isComplete)
    return t(TITLE_KEYS[kind].filled, {
      filled: levels.filter((level) => level.filled > 0).length,
      total: levels.length,
    });
  if (progress !== undefined)
    return t(TITLE_KEYS[kind].progress, {
      percent: formatFilledPercent(progress),
    });
  return t(TITLE_KEYS[kind].plain);
};

const ordersStats = (
  context: DetailsContext,
  kind: OrderKind,
  focus: number
): Stat[] => {
  const { orders, model, formatPrice, t } = context;
  const average = model.averageEntry;
  if (kind === "entry")
    return average === undefined
      ? []
      : [
          { label: t("trade_bar.avg_entry"), value: formatPrice(average) },
          ...optionalStat(
            t("trade_bar.vs_avg_entry"),
            orders.currentPrice
              ? change(orders, orders.currentPrice, average)
              : undefined
          ),
        ];
  const level = orders.takeProfits[focus];
  const fromAverage = change(orders, level.price, average);
  return [
    { label: t("trade_bar.price"), value: formatPrice(level.price) },
    ...(fromAverage
      ? optionalStat(t("trade_bar.from_avg_entry"), fromAverage)
      : optionalStat(
          t("trade_bar.from_price"),
          fromPrice(context, level.price)
        )),
  ];
};

const optionalStat = (label: string, value?: string): Stat[] =>
  value ? [{ label, value, change: true }] : [];

const levelStatus = (context: DetailsContext, level: LadderLevel) => {
  const { t } = context;
  if (level.filled >= 1) return t("trade_bar.filled");
  if (level.filled > 0)
    return t("trade_bar.filled_share", {
      percent: formatFilledPercent(level.filled),
    });
  return fromPrice(context, level.price) ?? t("trade_bar.not_filled");
};

const OrdersDetails = ({
  context,
  kind,
}: {
  context: DetailsContext;
  kind: OrderKind;
}) => {
  const levels = levelsOf(context.orders, kind);
  if (!levels.length) return <NoTakeProfit context={context} />;
  const focus = focusOf(context, kind);
  return (
    <>
      <Header title={ordersTitle(context, kind)} />
      {levels.length > TRADE_BAR_ROW_LIMIT && (
        <Strip context={context} kind={kind} focus={focus} />
      )}
      <Stats items={ordersStats(context, kind, focus)} />
      <OrderList context={context} kind={kind} focus={focus} />
    </>
  );
};

const Strip = ({
  context,
  kind,
  focus,
}: {
  context: DetailsContext;
  kind: OrderKind;
  focus: number;
}) => {
  const levels = levelsOf(context.orders, kind);
  const positions =
    kind === "entry" ? context.points.entries : context.points.takeProfits;
  const order = levels
    .map((_, index) => index)
    .sort((first, second) => positions[first] - positions[second]);
  return (
    <StripTrack $dense={levels.length > STRIP_DENSE_COUNT} aria-hidden={true}>
      {order.map((index) => (
        <StripCell
          key={index}
          $focused={index === focus}
          style={
            levels[index].filled > 0 && index !== focus
              ? { background: context.toneColor(kind) }
              : undefined
          }
        />
      ))}
    </StripTrack>
  );
};

const OrderList = ({
  context,
  kind,
  focus,
}: {
  context: DetailsContext;
  kind: OrderKind;
  focus: number;
}) => {
  const { showAll, onShowAllChange, t } = context;
  const levels = levelsOf(context.orders, kind);
  const next = nextIndexOf(context.orders, kind);
  const rows = getTradeBarOrderRows(levels, focus, next, showAll);
  return (
    <>
      {rows.map((row) => (
        <OrderRow
          key={row.type === "level" ? row.index : `${row.from}-${row.to}`}
          context={context}
          kind={kind}
          row={row}
          focus={focus}
          next={next}
        />
      ))}
      {onShowAllChange && levels.length > TRADE_BAR_ROW_LIMIT && (
        <ShowAll type="button" onClick={() => onShowAllChange(!showAll)}>
          {showAll
            ? t("trade_bar.show_less")
            : t("trade_bar.show_all", { count: levels.length })}
        </ShowAll>
      )}
    </>
  );
};

const OrderRow = ({
  context,
  kind,
  row,
  focus,
  next,
}: {
  context: DetailsContext;
  kind: OrderKind;
  row: TradeBarOrderRow;
  focus: number;
  next: number;
}) => {
  if (row.type === "group")
    return <GroupRow context={context} kind={kind} row={row} />;
  const level = levelsOf(context.orders, kind)[row.index];
  const isFocused = row.index === focus;
  const isDimmed = level.filled > 0 && !isFocused;
  const color = context.toneColor(kind);
  return (
    <Row $size={context.size} $focused={isFocused}>
      <Dot
        style={
          level.filled > 0
            ? { background: color }
            : { boxShadow: `inset 0 0 0 1.5px ${color}` }
        }
      />
      <RowName $muted={isDimmed} $strong={isFocused}>
        {levelName(context, kind, row.index)}
        {row.index === next && (
          <NextPill tone="green">{context.t("trade_bar.next")}</NextPill>
        )}
      </RowName>
      <RowPrice $muted={isDimmed}>{context.formatPrice(level.price)}</RowPrice>
      <RowMeta>{levelStatus(context, level)}</RowMeta>
    </Row>
  );
};

const GroupRow = ({
  context,
  kind,
  row,
}: {
  context: DetailsContext;
  kind: OrderKind;
  row: Extract<TradeBarOrderRow, { type: "group" }>;
}) => {
  const { t, formatPrice } = context;
  const levels = levelsOf(context.orders, kind);
  const count = row.to - row.from + 1;
  const isFilled = levels[row.from].filled > 0;
  const range = { from: row.from + 1, to: row.to + 1 };
  return (
    <Row $size={context.size} $focused={false} $tall={true}>
      <GroupMark style={{ borderColor: context.toneColor(kind) }} />
      <GroupText>
        <RowName $muted={true} $strong={false}>
          {kind === "entry"
            ? t("trade_bar.entry_range", range)
            : t("trade_bar.take_profit_range", range)}
        </RowName>
        <GroupRange>
          {formatPrice(levels[row.from].price)} –{" "}
          {formatPrice(levels[row.to].price)}
        </GroupRange>
      </GroupText>
      <span />
      <RowMeta>
        {isFilled
          ? t("trade_bar.count_filled", { count })
          : t("trade_bar.count_pending", { count })}
      </RowMeta>
    </Row>
  );
};

const priceLines = (context: DetailsContext): ValueLine[] => {
  const { orders, model, formatPrice, t } = context;
  const nextEntry = orders.entries[nextIndexOf(orders, "entry")];
  const nextTakeProfit = orders.takeProfits[nextIndexOf(orders, "take_profit")];
  const line = (key: string, name: string, price?: number) =>
    price === undefined
      ? []
      : [
          {
            key,
            label: `${name} · ${formatPrice(price)}`,
            value: fromPrice(context, price),
          },
        ];
  return [
    ...line(
      "take-profit",
      t("trade_bar.next_take_profit"),
      nextTakeProfit?.price
    ),
    ...line("average", t("trade_bar.avg_entry"), model.averageEntry),
    ...line("entry", t("trade_bar.next_entry"), nextEntry?.price),
    ...line("stop", t("trade_bar.stop_loss"), orders.stop?.price),
  ];
};

const PriceDetails = ({ context }: { context: DetailsContext }) => {
  const { orders, model, formatPrice, t } = context;
  const price = orders.currentPrice as number;
  const average = model.averageEntry;
  return (
    <>
      <Header
        title={
          orders.isClosed ? t("trade_bar.closed") : t("trade_bar.current_price")
        }
        aside={model.closeStatus}
      />
      <Stats
        items={[
          {
            label: orders.isClosed
              ? t("trade_bar.closed_price")
              : t("trade_bar.price"),
            value: formatPrice(price),
          },
          ...optionalStat(
            t("trade_bar.vs_avg_entry"),
            change(orders, price, average)
          ),
        ]}
      />
      <ValueLines lines={orders.isClosed ? [] : priceLines(context)} />
    </>
  );
};

const StopDetails = ({ context }: { context: DetailsContext }) => {
  const { orders, model, formatPrice, t } = context;
  if (!orders.stop) return <NoStop context={context} />;
  const price = orders.stop.price;
  return (
    <>
      <Header title={t("trade_bar.stop_loss")} />
      <Stats
        items={[
          { label: t("trade_bar.price"), value: formatPrice(price) },
          ...optionalStat(t("trade_bar.from_price"), fromPrice(context, price)),
        ]}
      />
      <ValueLines
        lines={[
          {
            key: "average",
            label: t("trade_bar.from_avg_entry"),
            value: change(orders, price, model.averageEntry),
          },
        ]}
      />
    </>
  );
};

const NoStop = ({ context }: { context: DetailsContext }) => {
  const { orders, formatPrice, t } = context;
  const lastEntry = orders.entries[orders.entries.length - 1];
  return (
    <>
      <Header
        title={t("trade_bar.stop_loss")}
        aside={
          <Pill tone="amber" icon={TriangleAlert}>
            {t("trade_bar.not_set")}
          </Pill>
        }
      />
      <Hint>{t("trade_bar.no_stop_hint")}</Hint>
      <ValueLines
        lines={
          lastEntry
            ? [
                {
                  key: "last-entry",
                  label: `${t("trade_bar.last_entry")} · ${formatPrice(
                    lastEntry.price
                  )}`,
                  value: fromPrice(context, lastEntry.price),
                },
              ]
            : []
        }
      />
    </>
  );
};

const NoTakeProfit = ({ context }: { context: DetailsContext }) => (
  <>
    <Header title={context.t("trade_bar.take_profits")} />
    <Hint>{context.t("trade_bar.no_take_profit")}</Hint>
  </>
);

const Header = ({ title, aside }: { title: string; aside?: ReactNode }) => (
  <HeaderRow>
    <HeaderTitle>{title}</HeaderTitle>
    {aside && <HeaderAside>{aside}</HeaderAside>}
  </HeaderRow>
);

const Stats = ({ items }: { items: Stat[] }) =>
  items.length ? (
    <StatsGrid>
      {items.map((item) => (
        <div key={item.label}>
          <StatLabel>{item.label}</StatLabel>
          <StatValue $change={item.change ? item.value : undefined}>
            {item.value}
          </StatValue>
        </div>
      ))}
    </StatsGrid>
  ) : null;

const ValueLines = ({ lines }: { lines: ValueLine[] }) => (
  <>
    {lines
      .filter((line) => line.value)
      .map((line) => (
        <ValueRow key={line.key}>
          <ValueLabel>{line.label}</ValueLabel>
          <ValueText>{line.value}</ValueText>
        </ValueRow>
      ))}
  </>
);

const STRIP_DENSE_COUNT = 20;
const ROW_HEIGHT = { card: 36, sheet: 44 };
const ROW_INSET = { card: 10, sheet: 12 };

const changeColor = (theme: DefaultTheme, value?: string) => {
  const tokens = getNewLookTokens(theme);
  if (!value) return tokens.ink;
  return value.startsWith("−") ? tokens.down : tokens.up;
};

const Root = styled.div<{ $size: TradeBarDetailsSize }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-variant-numeric: tabular-nums;
  --row-height: ${(p) => ROW_HEIGHT[p.$size]}px;
  --row-inset: ${(p) => ROW_INSET[p.$size]}px;
`;
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 22px;
  margin-bottom: 14px;
`;
const HeaderTitle = styled.span`
  ${newLookType.label};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const HeaderAside = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const StripTrack = styled.div<{ $dense: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: ${(p) => (p.$dense ? 1 : 3)}px;
  height: 8px;
  margin-bottom: 16px;
`;
const StripCell = styled.span<{ $focused: boolean }>`
  flex: 1;
  min-width: 1px;
  height: ${(p) => (p.$focused ? 8 : 4)}px;
  border-radius: 1px;
  background: ${(p) =>
    p.$focused
      ? getNewLookTokens(p.theme).ink
      : getNewLookTokens(p.theme).hairlineStrong};
`;
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-bottom: 14px;
  margin-bottom: 6px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const StatLabel = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const StatValue = styled.div<{ $change?: string }>`
  ${newLookType.figure};
  margin-top: 4px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) =>
    p.$change
      ? changeColor(p.theme, p.$change)
      : getNewLookTokens(p.theme).ink};
`;
const Row = styled.div<{
  $size: TradeBarDetailsSize;
  $focused: boolean;
  $tall?: boolean;
}>`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto ${(p) =>
      p.$size === "sheet" ? 72 : 64}px;
  align-items: center;
  gap: 0 8px;
  min-height: ${(p) =>
    p.$tall ? "calc(var(--row-height) + 8px)" : "var(--row-height)"};
  margin: 0 calc(var(--row-inset) * -1);
  padding: 0 var(--row-inset);
  border-radius: 8px;
  ${newLookType.label};
  ${(p) =>
    p.$focused &&
    css`
      background: ${getNewLookTokens(p.theme).rowHover};
    `}
`;
const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
`;
const RowName = styled.span<{ $muted: boolean; $strong: boolean }>`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  white-space: nowrap;
  color: ${(p) =>
    p.$muted
      ? getNewLookTokens(p.theme).secondary
      : getNewLookTokens(p.theme).ink};
  font-weight: ${(p) =>
    p.$strong ? p.theme.typography.fontWeights.MEDIUM : "inherit"};
`;
const NextPill = styled(Pill)`
  margin-left: 6px;
  padding: 0 6px;
  font-size: 10.5px;
`;
const RowPrice = styled.span<{ $muted: boolean }>`
  text-align: right;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  color: ${(p) =>
    p.$muted
      ? getNewLookTokens(p.theme).secondary
      : getNewLookTokens(p.theme).ink};
`;
const RowMeta = styled.span`
  ${newLookType.meta};
  text-align: right;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const GroupMark = styled.span`
  justify-self: center;
  height: 18px;
  border-left: 2px dotted;
`;
const GroupText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const GroupRange = styled.span`
  ${newLookType.meta};
  margin-top: 2px;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const ShowAll = styled.button`
  ${newLookType.label};
  margin-top: 8px;
  padding: 12px 0 4px;
  border: none;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: transparent;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  cursor: pointer;
`;
const ValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: var(--row-height);
  ${newLookType.label};
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const ValueLabel = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const ValueText = styled.span`
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Hint = styled.p`
  ${newLookType.label};
  margin: 0 0 8px;
  line-height: 1.6;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
